import React, { Children, cloneElement, useMemo } from 'react';

import { Box } from '@rover/kibble/core';

import {
  AccordionContext,
  AccordionItemElement,
  AccordionProps,
  getIsOpenByIndex,
} from './Accordion.common';
import AccordionItem from './AccordionItem';

const Accordion: React.FC<AccordionProps> = ({
  children,
  accordionItems,
  itemColorVariant = 'primary',
  headingSize = '300',
  itemSchemaProps,
  bodySchemaProps,
  defaultOpen = 'none',
  ...boxProps
}) => {
  const contextValue = useMemo(
    () => ({ itemColorVariant, headingSize, itemSchemaProps, bodySchemaProps }),
    [itemColorVariant, headingSize, itemSchemaProps, bodySchemaProps]
  );

  return (
    <Box webAs="dl" data-testid="accordion-wrapper" {...boxProps}>
      <AccordionContext.Provider value={contextValue}>
        {accordionItems?.map((itemProps, index) => (
          <AccordionItem key={index} {...itemProps} isOpen={getIsOpenByIndex(index, defaultOpen)} />
        ))}

        {/* It's not recommended to use React.Children - see https://react.dev/reference/react/Children
         * This is a case where React.Children & cloneElement give us exactly what is needed, where the suggested alternatives don't. We are being cautious about how we mutate these elements in order to avoid the documented pitfalls.
         * Please don't reuse this pattern unless you are human and you know what you are doing.  */}
        {Children.map(Children.toArray(children), (child, index) =>
          cloneElement(child as AccordionItemElement, {
            isOpen:
              typeof accordionItems === 'undefined'
                ? getIsOpenByIndex(index, defaultOpen)
                : undefined,
          })
        )}
      </AccordionContext.Provider>
    </Box>
  );
};

export default Accordion;
